import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import RecipeResultCard from "components/RecipeResultCard";
import Recipe1Page from "layouts/module/components/RecipeDetails";
import apiClient from "utils/apiClient";
import { getAuthToken } from "utils/authToken";
import { ENDPOINTS } from "utils/endpoints";
import { useCatalogStats, formatCatalogCounts } from "utils/catalogStats";
import { dietFilterValue, recipeDietLabel, useDietPreference } from "utils/dietPreference";
import { RED, RED_LIGHT, GREEN, BORDER, MUTED } from "assets/theme/brandColors";
import { MEAL_TYPE_BADGE as MEAL_TYPE_STYLE } from "utils/mealTypes";
import { passiveWaitBadge } from "utils/passiveWait";

// Matches TIME_BANDS in back-end/schedule/catalog.py. The backend owns the
// edges; these are only the labels for them.
const TIME_BANDS = [
  { value: "quick", label: "Under 30 min" },
  { value: "medium", label: "30–60 min" },
  { value: "long", label: "Over an hour" },
];

// Same badge palette the schedule builder and cookbook already use for meal
// types, so a "Main" chip means the same thing and looks the same everywhere.

const DIFFICULTY_LABELS = { 1: "Easy", 2: "Easy", 3: "Medium", 4: "Hard", 5: "Hard" };

const EMPTY_FILTERS = { cuisine: null, diet: null, meal_type: null, time_band: null };

// ── Filter chip ────────────────────────────────────────────────────────────
function FilterChip({ label, active, onClick }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        px: 1.5,
        py: "6px",
        borderRadius: 20,
        border: "1.5px solid",
        borderColor: active ? RED : BORDER,
        bgcolor: active ? RED_LIGHT : "#fff",
        color: active ? RED : "#444",
        fontSize: 12.5,
        fontWeight: active ? 700 : 500,
        cursor: "pointer",
        fontFamily: "Roboto, sans-serif",
        whiteSpace: "nowrap",
        transition: "all 0.12s",
        "&:hover": { borderColor: RED },
      }}
    >
      {label}
    </Box>
  );
}
FilterChip.propTypes = {
  label: PropTypes.string.isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

// ── Filter row ─────────────────────────────────────────────────────────────
function FilterRow({ title, options, value, onChange }) {
  if (!options || options.length === 0) return null;
  return (
    <Box sx={{ mb: 1.75 }}>
      <Box
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: MUTED,
          textTransform: "uppercase",
          letterSpacing: "0.7px",
          mb: 0.75,
          fontFamily: "Roboto, sans-serif",
        }}
      >
        {title}
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
        {options.map((o) => (
          <FilterChip
            key={o.value}
            label={o.label}
            active={value === o.value}
            // Tapping the active chip clears it, so every filter is
            // reversible without a separate "all" chip per row.
            onClick={() => onChange(value === o.value ? null : o.value)}
          />
        ))}
      </Box>
    </Box>
  );
}
FilterRow.propTypes = {
  title: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired })
  ),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

// ── Page ───────────────────────────────────────────────────────────────────
function BrowseRecipes() {
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Recipes the user picked. Kept as whole card objects rather than bare ids so
  // the tray and the builder can name them without another round trip.
  const [picks, setPicks] = useState([]);
  const [detailRecipe, setDetailRecipe] = useState(null);
  const navigate = useNavigate();
  const stats = useCatalogStats();
  // The existing diet filter, defaulted from the profile setting rather than
  // rebuilt: browse deliberately shows the catalog as it is (nothing per-user
  // reaches schedule/catalog.py), so this is a pre-selected chip the user can
  // clear, not a hidden exclusion. Picks made here still go through the builder,
  // where the diet setting is a real filter.
  const { diet: profileDiet, loading: dietLoading } = useDietPreference();
  const [dietDefaulted, setDietDefaulted] = useState(false);

  useEffect(() => {
    if (dietLoading || dietDefaulted) return;
    const value = dietFilterValue(profileDiet);
    if (value) setFilters((prev) => ({ ...prev, diet: value }));
    setDietDefaulted(true);
  }, [dietLoading, dietDefaulted, profileDiet]);

  useEffect(() => {
    if (!getAuthToken()) navigate("/authentication/sign-in", { replace: true });
  }, [navigate]);

  // Debounced search. Typing "chicken" shouldn't be six requests, and each
  // keystroke landing out of order is how a search box ends up showing results
  // for a prefix of what's in it.
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    apiClient
      .get(ENDPOINTS.schedule.catalogFilters)
      .then((res) => setOptions(res.data))
      .catch((err) => console.error("Failed to load filter options:", err.message));
  }, []);

  useEffect(() => {
    // Held until the profile setting has been applied, so a vegan never sees a
    // page of meat flash past before the default lands.
    if (!dietDefaulted) return undefined;
    let cancelled = false;
    setLoading(true);
    apiClient
      .get(ENDPOINTS.schedule.catalog, {
        params: {
          page,
          page_size: 10,
          ...(query ? { q: query } : {}),
          ...(filters.cuisine ? { cuisine: filters.cuisine } : {}),
          ...(filters.diet ? { diet: filters.diet } : {}),
          ...(filters.meal_type ? { meal_type: filters.meal_type } : {}),
          ...(filters.time_band ? { time_band: filters.time_band } : {}),
        },
      })
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (!cancelled) setData({ results: [], total: 0, total_pages: 0 });
        console.error("Failed to load catalog:", err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, query, filters, dietDefaulted]);

  // Changing a filter changes the result set, so page 1 is the only page that
  // is guaranteed to exist afterwards. Staying on page 4 of a set that now has
  // two pages shows an empty list and reads as "no recipes".
  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const clearAll = () => {
    setFilters(EMPTY_FILTERS);
    setSearchInput("");
    setQuery("");
    setPage(1);
  };

  const togglePick = (recipe) => {
    setPicks((prev) =>
      prev.find((p) => p.id === recipe.id)
        ? prev.filter((p) => p.id !== recipe.id)
        : [...prev, recipe]
    );
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length + (query ? 1 : 0);

  const countsLine = formatCatalogCounts(stats);
  const totalPages = data?.total_pages || 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box
        sx={{
          pt: 3,
          // Room for the picks tray, which is fixed to the bottom of the
          // viewport and would otherwise cover the last card.
          pb: picks.length > 0 ? 14 : 6,
          px: { xs: 0, md: 3 },
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 720 }}>
          {/* Header */}
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: { xs: "10px", md: "14px" },
              border: "1.5px solid",
              borderColor: BORDER,
              p: { xs: "18px 16px", md: "26px 28px" },
              mb: 2.5,
            }}
          >
            <Box
              sx={{
                fontSize: 20,
                fontWeight: 800,
                color: "#222",
                fontFamily: "Roboto, sans-serif",
                mb: 0.5,
              }}
            >
              Browse recipes
            </Box>
            <Box
              sx={{
                fontSize: 13,
                color: MUTED,
                fontFamily: "Roboto, sans-serif",
                mb: 2.5,
              }}
            >
              {countsLine || "Everything in the catalogue."}
            </Box>

            {/* Search */}
            <Box
              component="input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search recipes…"
              sx={{
                width: "100%",
                padding: "10px 14px",
                border: "1.5px solid",
                borderColor: BORDER,
                borderRadius: "8px",
                fontSize: 14,
                fontFamily: "Roboto, sans-serif",
                outline: "none",
                color: "#222",
                bgcolor: "#fff",
                "&:focus": { borderColor: RED },
                "&::placeholder": { color: MUTED },
                transition: "border-color 0.15s",
              }}
            />

            {/* Filter toggle */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mt: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Box
                component="button"
                onClick={() => setFiltersOpen((v) => !v)}
                sx={{
                  px: 1.5,
                  py: "7px",
                  borderRadius: "8px",
                  border: "1.5px solid",
                  borderColor: activeFilterCount ? RED : BORDER,
                  bgcolor: activeFilterCount ? RED_LIGHT : "#fff",
                  color: activeFilterCount ? RED : "#444",
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "Roboto, sans-serif",
                  "&:hover": { borderColor: RED },
                }}
              >
                {filtersOpen ? "Hide filters" : "Filters"}
                {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
              </Box>
              {activeFilterCount > 0 && (
                <Box
                  component="button"
                  onClick={clearAll}
                  sx={{
                    px: 1.25,
                    py: "7px",
                    border: "none",
                    bgcolor: "transparent",
                    color: MUTED,
                    fontSize: 12.5,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "Roboto, sans-serif",
                    "&:hover": { color: "#222" },
                  }}
                >
                  Clear all
                </Box>
              )}
            </Box>

            {filtersOpen && (
              <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: BORDER }}>
                <FilterRow
                  title="Cuisine"
                  options={(options?.cuisines || []).map((c) => ({
                    value: c.value,
                    label: c.value,
                  }))}
                  value={filters.cuisine}
                  onChange={(v) => setFilter("cuisine", v)}
                />
                <FilterRow
                  title="Diet"
                  options={(options?.diets || []).map((d) => ({
                    value: d.value,
                    label: recipeDietLabel(d.value),
                  }))}
                  value={filters.diet}
                  onChange={(v) => setFilter("diet", v)}
                />
                <FilterRow
                  title="Meal type"
                  options={(options?.meal_types || []).map((m) => ({
                    value: m.value,
                    label: MEAL_TYPE_STYLE[m.value]?.label || m.value,
                  }))}
                  value={filters.meal_type}
                  onChange={(v) => setFilter("meal_type", v)}
                />
                <FilterRow
                  title="Time"
                  options={TIME_BANDS}
                  value={filters.time_band}
                  onChange={(v) => setFilter("time_band", v)}
                />
              </Box>
            )}
          </Box>

          {/* Results */}
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: { xs: "10px", md: "14px" },
              border: "1.5px solid",
              borderColor: BORDER,
              p: { xs: "16px 14px", md: "22px 24px" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 1,
                mb: 1.75,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#222",
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                {loading ? "Loading…" : `${data?.total || 0} recipe${data?.total === 1 ? "" : "s"}`}
              </Box>
              {totalPages > 1 && (
                <Box sx={{ fontSize: 12, color: MUTED, fontFamily: "Roboto, sans-serif" }}>
                  Page {page} of {totalPages}
                </Box>
              )}
            </Box>

            {!loading && (data?.results || []).length === 0 && (
              <Box
                sx={{
                  py: 4,
                  textAlign: "center",
                  fontSize: 13,
                  color: MUTED,
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                Nothing matches those filters. Try clearing one.
              </Box>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {(data?.results || []).map((recipe) => (
                <RecipeResultCard
                  key={recipe.id}
                  title={recipe.recipe_title}
                  badge={MEAL_TYPE_STYLE[recipe.meal_type]}
                  extraBadges={[passiveWaitBadge(recipe.passive_wait_minutes)]}
                  meta={[
                    recipe.cuisine,
                    recipe.time_in_minutes ? `${recipe.time_in_minutes} min` : null,
                    recipe.servings ? `serves ${recipe.servings}` : null,
                    recipe.xp ? `${recipe.xp} XP` : null,
                    DIFFICULTY_LABELS[recipe.difficulty],
                  ]}
                  description={recipe.description}
                  selectable
                  selected={!!picks.find((p) => p.id === recipe.id)}
                  onToggleSelect={() => togglePick(recipe)}
                  onClick={() => setDetailRecipe(recipe)}
                />
              ))}
            </Box>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                  mt: 2.5,
                  pt: 2,
                  borderTop: "1px solid",
                  borderColor: BORDER,
                }}
              >
                <PageButton
                  label="← Previous"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                />
                <Box
                  sx={{
                    fontSize: 12.5,
                    color: MUTED,
                    fontFamily: "Roboto, sans-serif",
                    minWidth: 90,
                    textAlign: "center",
                  }}
                >
                  {page} / {totalPages}
                </Box>
                <PageButton
                  label="Next →"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Picks tray. Sits above everything so a selection made on page 1 is
          still visible after paging to page 4 — the picks survive paging and
          filtering, only the list underneath changes. */}
      {picks.length > 0 && (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1300,
            bgcolor: "#fff",
            borderTop: "1.5px solid",
            borderColor: BORDER,
            p: { xs: "12px 14px", md: "14px 24px" },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 180, maxWidth: 420 }}>
            <Box
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: "#222",
                fontFamily: "Roboto, sans-serif",
              }}
            >
              {picks.length} recipe{picks.length === 1 ? "" : "s"} picked
            </Box>
            <Box
              sx={{
                fontSize: 11.5,
                color: MUTED,
                fontFamily: "Roboto, sans-serif",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {picks.map((p) => p.recipe_title).join(", ")}
            </Box>
          </Box>
          <Box
            component="button"
            onClick={() => setPicks([])}
            sx={{
              px: 1.75,
              py: "10px",
              bgcolor: "#f0f0f0",
              color: "#444",
              border: "none",
              borderRadius: "8px",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "Roboto, sans-serif",
              "&:hover": { bgcolor: "#e4e4e4" },
            }}
          >
            Clear
          </Box>
          <Box
            component="button"
            onClick={() => navigate("/schedule-builder", { state: { lockedRecipes: picks } })}
            sx={{
              px: 2.25,
              py: "10px",
              bgcolor: RED,
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "Roboto, sans-serif",
              "&:hover": { bgcolor: "#7d1520" },
            }}
          >
            Build a schedule
          </Box>
        </Box>
      )}

      {/* Read-only detail. Reuses the cooking view's recipe renderer in
          preview mode, so ingredients go through the one formatter and the
          steps read exactly as they will when the recipe is actually cooked. */}
      {detailRecipe && (
        <Box
          onClick={() => setDetailRecipe(null)}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.45)",
            zIndex: 1400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 1, md: 2 },
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              bgcolor: "#fff",
              borderRadius: "14px",
              maxWidth: 900,
              width: "100%",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                p: "16px 20px",
                borderBottom: "1px solid",
                borderColor: BORDER,
              }}
            >
              <Box
                sx={{
                  fontSize: 15,
                  fontWeight: 800,
                  color: "#222",
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                {detailRecipe.recipe_title}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  component="button"
                  onClick={() => togglePick(detailRecipe)}
                  sx={{
                    px: 1.75,
                    py: "8px",
                    border: "1.5px solid",
                    borderColor: picks.find((p) => p.id === detailRecipe.id) ? GREEN : BORDER,
                    bgcolor: picks.find((p) => p.id === detailRecipe.id) ? GREEN : "#fff",
                    color: picks.find((p) => p.id === detailRecipe.id) ? "#fff" : "#444",
                    borderRadius: "8px",
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: "Roboto, sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  {picks.find((p) => p.id === detailRecipe.id) ? "✓ Picked" : "Pick this"}
                </Box>
                <Box
                  component="span"
                  onClick={() => setDetailRecipe(null)}
                  sx={{
                    fontSize: 20,
                    color: MUTED,
                    cursor: "pointer",
                    lineHeight: 1,
                    px: 0.5,
                    "&:hover": { color: "#222" },
                  }}
                >
                  ×
                </Box>
              </Box>
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 1.5, md: 2.5 } }}>
              <Recipe1Page
                recipeId={detailRecipe.id}
                isCompleted={false}
                onMarkCompleted={() => {}}
                previewOnly
              />
            </Box>
          </Box>
        </Box>
      )}
    </DashboardLayout>
  );
}

function PageButton({ label, disabled, onClick }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        px: 1.75,
        py: "8px",
        border: "1.5px solid",
        borderColor: BORDER,
        bgcolor: "#fff",
        // MUTED is the palette's floor for readable text; a disabled control
        // still has to be legible, so it keeps MUTED and loses the affordance
        // (border, hover, cursor) instead of fading toward the background.
        color: disabled ? MUTED : "#222",
        borderRadius: "8px",
        fontSize: 12.5,
        fontWeight: 700,
        cursor: disabled ? "default" : "pointer",
        fontFamily: "Roboto, sans-serif",
        "&:hover": disabled ? {} : { borderColor: RED, color: RED },
      }}
    >
      {label}
    </Box>
  );
}
PageButton.propTypes = {
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export default BrowseRecipes;
